/**
 * The kept bank connections of one person at one firm, as the signed-in snapshot form shows
 * and removes them (kept connection slice S5, written 2026-10-08).
 *
 * Two things live here, and both are pure so the façade and the tests share one reading:
 *
 *   - {@link IntakeKeptConnectionSchema}: the ONE shape the draft read returns for a kept
 *     connection. It carries no secret and no provider id: a kept id (ours), the bank's display
 *     name, the date the connection is due to end, and the ids of the draft's snapshot accounts
 *     that came from it. Appcore's member list (`GET /me/kept-connections`, read from appcore
 *     origin/main on 2026-10-08) carries `keptId, status, firmWorkspaceId, institutionName?,
 *     accounts[name,type,mask?,subtype?], createdAt, removeAfter`; `subtype` is read by the
 *     duplicate match (`keptEndOlder.ts`) and not by this view, which is built from the other
 *     fields only.
 *
 *   - {@link matchKeptAccounts}: which snapshot accounts came from which kept connection.
 *
 * ## What the match reads: type, mask and name, and the bank when BOTH sides name one
 *
 * The member list does not return a kept account's Plaid `accountId` (its account objects are
 * `name, type, mask` and, since appcore#816, `subtype`), and the draft's snapshot account does not
 * carry the kept id. So the account is matched on what the bank shows: its type, mask and name
 * (compared trimmed and lower-cased, key `type|mask|name`). The kept row's accounts come from
 * `keptAccountRecords` and the snapshot's from `mapAccount` (`services/kept-link/src/plaidSnapshot.ts`,
 * read 2026-10-08), two code paths over one Plaid payload, and nothing pins that their `name`
 * strings stay equal. The snapshot this function reads is the STORED draft value, which the
 * browser saved back after merging the link's answer into it (`useSignedInDraft.ts`, read
 * 2026-10-08), so it is the person's own draft and not a record kept-link wrote and nobody
 * touched. That makes every comparison here a CORRECTNESS guard on the person's own draft, not an
 * authorisation check: ending a connection is by kept id against the caller's own records, and
 * this match only decides which of the person's form accounts are queued off with it. A drift
 * between the two sides fails SAFE for ending the connection and fails OPEN for the accounts: an
 * account this match misses stays on the form after "Remove this bank", where its own Remove still
 * works. The tests pin the match in both directions; they cannot pin the two services' agreement.
 *
 * ## The bank (added 2026-10-08, after a deployed-dev observation)
 *
 * The type|mask|name key has no bank in it, and Plaid's sandbox gives two different banks
 * accounts with identical names, masks and types. Without a bank check the newest record claimed
 * every other bank's account that read the same, so "Remove this bank" on the newer bank also
 * queued the older bank's accounts off the form (observed in a browser on deployed dev,
 * 2026-10-08). The bank is compared in TWO passes, each over the records newest first:
 *
 *   1. A record claims an account only when BOTH it and the account name a bank, the two are
 *      equal after the same trim and lower-case, and the key matches. So a newer record with no
 *      bank name cannot take an account from an older record that holds the same key and whose
 *      bank name matches the account's exactly. (An older named record that does NOT hold the
 *      key claims nothing here, and the nameless record can still take the account in pass 2.)
 *   2. Every account still unclaimed goes by the key, with the bank check still refusing a
 *      named record against an account that names a DIFFERENT bank (so an account naming a bank
 *      that matches no record can go only to a record with no name). Where either side has no name (absent, or blank after trimming) the
 *      key alone decides, as it did before the bank check.
 *
 * A record's ids come back in the snapshot's own order, whichever pass claimed them.
 *
 * What the two bank strings are, read 2026-10-08 in `plaidSnapshot.ts`: the kept row's
 * `institutionName` is the Item's own `institution_name` from `/accounts/get` (cleaned by
 * `displayText`, at most 120 characters). The snapshot account's `institution` is, for an Item
 * that was kept and whose row was given that name, the SAME value (the stamp follows what was
 * handed to `keep`); in any other case it is the `/statements/list` `institution_name` when that
 * names one, and otherwise the Item name. The kept-link suite pins that equality for a kept Item
 * whose statements list spells the bank differently; nothing here can, and a record or account
 * stored before that change can still carry two spellings. When the two differ for one real
 * bank, the record claims none of its own accounts (pass 1 refuses them, and in pass 2 the bank
 * check refuses them too). What the person then sees: the bank is listed with no accounts under
 * it, and "Remove this bank" ends the connection (200) while its accounts stay on the form, each
 * with its own Remove. Also read 2026-10-08: `narrowKeptMemberList` drops an `institutionName`
 * longer than 120 characters, which makes that record count as having none.
 *
 * An account is given to ONE connection. When two kept connections hold an account that reads
 * the same (the same bank linked twice, whose second link replaced the first's accounts on the
 * form), the NEWER connection (later `createdAt`, then greater kept id) gets it: it is the link
 * whose pull the form is showing. Removing the older connection then removes no draft account.
 * That holds only if a caller passes ALL of one firm's records at once: the façade's draft read and
 * its remove route both do, through `matchOwnKept` (`steward-app-api/src/services/keptConnections.ts`),
 * and `intakeKeptRemove.test.ts` pins the split on both. What it still CANNOT do (appcore's list
 * carries no account ids and no institution id): tell apart two snapshot accounts with the SAME
 * key at a bank with the SAME display NAME (two different institutions that share a name are one
 * bank here), or where either side names no bank. So when two connections each hold one such
 * account, the newest connection claims BOTH (`intakeKeptRemove.test.ts`, "KNOWN LIMIT"). Called
 * with ONE record, this function gives it every account it matches, which is how the route once
 * queued the newer link's accounts.
 */
import { z } from 'zod';
import { ULID_PATTERN } from './intakeMessage.js';
import { IntakeSnapshotAccountIdSchema } from './intakeSnapshot.js';

/** The statuses of a kept connection that the form lists. `pending` is a link in progress or a
 *  leftover the remover clears, and `moving` belongs to a mover that does not exist yet. */
export const INTAKE_KEPT_LISTED_STATUSES = ['kept', 'removing'] as const;

/** One kept connection as the draft read returns it. Strict: an extra key is a refusal. */
export const IntakeKeptConnectionSchema = z.strictObject({
  /** Ours, opaque to the browser; the id the remove route takes. */
  keptId: z.string().regex(ULID_PATTERN),
  /** The bank's display name, when appcore stored one. */
  institutionName: z.string().min(1).max(120).optional(),
  /** When the connection is due to end by itself, ISO 8601. */
  removeAfter: z.string().min(1).max(64),
  /** The ids of the draft's snapshot accounts matched to this connection. May be empty. */
  accountIds: z.array(IntakeSnapshotAccountIdSchema).max(50),
});

export type IntakeKeptConnection = z.infer<typeof IntakeKeptConnectionSchema>;

/** The keys of {@link IntakeKeptConnectionSchema}, for the test that compares them with the
 *  façade's builder and the member-list fields. */
export const INTAKE_KEPT_CONNECTION_KEYS = ['keptId', 'institutionName', 'removeAfter', 'accountIds'] as const;

/** What the member list gives us for one kept connection, narrowed. */
export interface KeptMemberRecord {
  keptId: string;
  status: string;
  firmWorkspaceId: string;
  institutionName?: string;
  createdAt: string;
  removeAfter: string;
  accounts: { name: string; type: string; mask?: string; subtype?: string }[];
}

const NAME_MAX = 120;

function str(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined;
}

/**
 * Narrow appcore's `GET /me/kept-connections` answer (`{ data: { connections: [...] } }`).
 * Returns `null` for an answer that does not have the shape, so a caller treats it as "unknown"
 * and never as "none". A single malformed connection is dropped rather than failing the list:
 * the list is what the person is shown, and one row we cannot read must not hide the rest. A
 * dropped row is a bank we cannot show or end from the form, which is why the caller counts it.
 */
export function narrowKeptMemberList(answer: unknown): { records: KeptMemberRecord[]; dropped: number } | null {
  const data = (answer as { data?: unknown } | null | undefined)?.data;
  const list = (data as { connections?: unknown } | null | undefined)?.connections;
  if (!Array.isArray(list)) return null;
  const records: KeptMemberRecord[] = [];
  let dropped = 0;
  for (const entry of list) {
    if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) { dropped += 1; continue; }
    const e = entry as Record<string, unknown>;
    const keptId = str(e['keptId']);
    const status = str(e['status']);
    const firmWorkspaceId = str(e['firmWorkspaceId']);
    const createdAt = str(e['createdAt']);
    const removeAfter = str(e['removeAfter']);
    if (!keptId || !ULID_PATTERN.test(keptId) || !status || !firmWorkspaceId || !createdAt || !removeAfter) {
      dropped += 1;
      continue;
    }
    const accounts: KeptMemberRecord['accounts'] = [];
    for (const a of Array.isArray(e['accounts']) ? e['accounts'] : []) {
      if (typeof a !== 'object' || a === null) continue;
      const name = str((a as Record<string, unknown>)['name']);
      const type = str((a as Record<string, unknown>)['type']);
      const mask = str((a as Record<string, unknown>)['mask']);
      // Read only when the list supplies one, as a non-empty string. Used by the duplicate
      // match (`keptEndOlder.ts`); `matchKeptAccounts` and the views do not read it.
      const subtype = str((a as Record<string, unknown>)['subtype']);
      if (name && type) accounts.push({ name, type, ...(mask ? { mask } : {}), ...(subtype ? { subtype } : {}) });
    }
    const institutionName = str(e['institutionName']);
    records.push({
      keptId, status, firmWorkspaceId, createdAt, removeAfter, accounts,
      ...(institutionName && institutionName.length <= NAME_MAX ? { institutionName } : {}),
    });
  }
  return { records, dropped };
}

/** The connections the form lists for one firm: this firm's, in a listed status. */
export function keptRecordsForFirm(records: readonly KeptMemberRecord[], firmWorkspaceId: string): KeptMemberRecord[] {
  return records.filter((r) => r.firmWorkspaceId === firmWorkspaceId
    && (INTAKE_KEPT_LISTED_STATUSES as readonly string[]).includes(r.status));
}

const norm = (value: string | undefined | null): string => (value ?? '').trim().toLowerCase();
const accountKey = (name: string, mask: string | undefined | null, type: string): string =>
  `${norm(type)}|${norm(mask)}|${norm(name)}`;

/**
 * For each of `records` (assumed to be one firm's), the ids of the snapshot accounts that came
 * from it. Reads `snapshot` defensively: it is a stored value. Every record gets an entry, empty
 * when nothing matched. See the file header for the rule and for what it cannot show.
 */
export function matchKeptAccounts(
  records: readonly KeptMemberRecord[], snapshot: unknown,
): Map<string, string[]> {
  const out = new Map<string, string[]>(records.map((r) => [r.keptId, []]));
  const accounts = typeof snapshot === 'object' && snapshot !== null
    && Array.isArray((snapshot as Record<string, unknown>)['accounts'])
    ? (snapshot as { accounts: unknown[] }).accounts : [];
  // Newest first, so the first record to claim an account is the one whose pull the form shows.
  const newestFirst = [...records].sort((a, b) => (a.createdAt === b.createdAt
    ? (a.keptId < b.keptId ? 1 : -1)
    : (a.createdAt < b.createdAt ? 1 : -1)));
  const owner = new Map<string, string>();
  // Two passes. Pass 1 (`named`): across ALL records, newest first, a record claims an account only
  // when BOTH sides name a bank, the two are equal, and the key matches; so a newer record with no
  // bank name cannot take an account away from an older record holding the same key whose bank name matches it exactly.
  // Pass 2: what is still unclaimed goes by the key, newest first, with the bank check still
  // refusing a named record against an account that names a different bank; so a named record is
  // never handed an account whose bank matches no record, and a side with no name falls back to
  // the key alone.
  for (const named of [true, false]) {
    for (const record of newestFirst) {
      const wanted = new Set(record.accounts.map((a) => accountKey(a.name, a.mask, a.type)));
      const recordBank = norm(record.institutionName);
      if (named && recordBank === '') continue;
      for (const entry of accounts) {
        if (typeof entry !== 'object' || entry === null) continue;
        const a = entry as Record<string, unknown>;
        const id = a['accountId'];
        if (typeof id !== 'string' || id === '' || owner.has(id)) continue;
        if (typeof a['name'] !== 'string' || typeof a['type'] !== 'string') continue;
        const mask = typeof a['mask'] === 'string' ? a['mask'] : undefined;
        if (!wanted.has(accountKey(a['name'], mask, a['type']))) continue;
        const accountBank = typeof a['institution'] === 'string' ? norm(a['institution']) : '';
        if (named ? accountBank !== recordBank || accountBank === '' : (recordBank !== '' && accountBank !== '' && recordBank !== accountBank)) continue;
        owner.set(id, record.keptId);
      }
    }
  }
  // Each record's ids in the snapshot's own order, whichever pass claimed them.
  const seen = new Set<string>();
  for (const entry of accounts) {
    const id = (entry as Record<string, unknown> | null)?.['accountId'];
    if (typeof id !== 'string' || seen.has(id)) continue;
    seen.add(id);
    const keptId = owner.get(id);
    if (keptId !== undefined) out.get(keptId)!.push(id);
  }
  return out;
}

/** The view the draft read returns, with no secret and no provider id in it. */
export function toKeptConnectionViews(
  records: readonly KeptMemberRecord[], matched: ReadonlyMap<string, string[]>,
): IntakeKeptConnection[] {
  return records.map((r) => ({
    keptId: r.keptId,
    ...(r.institutionName ? { institutionName: r.institutionName } : {}),
    removeAfter: r.removeAfter,
    accountIds: (matched.get(r.keptId) ?? []).slice(0, 50),
  }));
}
